// Reports recruiter keys a non-English language is missing, so a gap that
// `yarn validate:i18n` cannot see (a key built at runtime, a bundle shipped
// without it) shows up in Datadog instead of as silent English. Recruiter-only:
// index.ts registers it; the candidate apps never import this module.
import { datadogRum } from '@datadog/browser-rum';

import i18n, {
  registerResolvedTranslationListener,
  type ResolvedTranslation,
} from '../i18n';
import { RECRUITER_NAMESPACES, type RecruiterNamespace } from './languages';

/** Share of distinct missing keys reported; each key is considered once per page load. */
const MISSING_KEY_SAMPLE_RATE = 0.1;

/** The RUM action name; its context is a MissingKeyReport. */
const MISSING_KEY_ACTION = 'i18n_missing_key';

export interface MissingKeyReport {
  language: string;
  namespace: RecruiterNamespace;
  key: string;
  /** `fallback`: shown from another language; `absent`: no language has it. */
  kind: 'fallback' | 'absent';
  /** The language the text came from (`en` for a fallback). */
  shownLanguage: string | undefined;
}

const RECRUITER_NAMESPACE_SET: ReadonlySet<string> = new Set(
  RECRUITER_NAMESPACES
);

const isRecruiterNamespace = (
  namespace: string | undefined
): namespace is RecruiterNamespace =>
  namespace !== undefined && RECRUITER_NAMESPACE_SET.has(namespace);

/** `nl` for `nl-BE`: a regional language reads its base bundle on purpose. */
const baseLanguage = (language: string): string =>
  language.split('-')[0]?.toLowerCase() ?? language;

/**
 * The report for a translation, or null when it came from the requested
 * language (or its base language), the language is English, or the key is not
 * a recruiter one.
 */
export function missingKeyReport(
  translation: ResolvedTranslation
): MissingKeyReport | null {
  const language = baseLanguage(translation.language);
  if (language === 'en' || language === 'cimode') return null;
  if (!isRecruiterNamespace(translation.usedNamespace)) return null;
  const shownLanguage = translation.usedLanguage;
  let kind: MissingKeyReport['kind'];
  if (!translation.found) {
    kind = 'absent';
  } else if (shownLanguage && baseLanguage(shownLanguage) !== language) {
    kind = 'fallback';
  } else {
    return null;
  }
  return {
    language: translation.language,
    namespace: translation.usedNamespace,
    key: translation.key,
    kind,
    shownLanguage,
  };
}

export interface MissingKeyReporterDeps {
  /** True when the (base) language's bundle for the namespace has loaded. */
  hasBundle: (language: string, namespace: string) => boolean;
  /** A number in [0, 1): reports when below the sample rate. */
  random: () => number;
  send: (report: MissingKeyReport) => void;
  sampleRate?: number;
}

/**
 * A listener that sends a sample of distinct missing keys. A namespace still
 * loading (staff strings, a slow chunk) falls back to English by design, so it
 * is not reported.
 */
export function createMissingKeyReporter({
  hasBundle,
  random,
  send,
  sampleRate = MISSING_KEY_SAMPLE_RATE,
}: MissingKeyReporterDeps): (translation: ResolvedTranslation) => void {
  const considered = new Set<string>();
  return (translation) => {
    const report = missingKeyReport(translation);
    if (
      !report ||
      !hasBundle(baseLanguage(report.language), report.namespace)
    ) {
      return;
    }
    const id = `${report.language}|${report.namespace}|${report.key}`;
    if (considered.has(id)) return;
    considered.add(id);
    if (random() < sampleRate) send(report);
  };
}

/** Starts reporting for the recruiter app. Tests keep the default (no listener). */
export function startMissingKeyReporting(): void {
  if (import.meta.env.MODE === 'test') return;
  registerResolvedTranslationListener(
    createMissingKeyReporter({
      hasBundle: (language, namespace) =>
        i18n.hasResourceBundle(language, namespace),
      random: Math.random,
      send: (report) => {
        datadogRum.addAction(MISSING_KEY_ACTION, { ...report });
      },
    })
  );
}
